import React, { Fragment, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import ActionButtons from "./ActionButtons";
import downarrow from "../../assets/svg/dropdownarrow.svg";
import rightArrow from "../../assets/svg/rightArrowActive.svg";
import { ArrowUpwardIcon } from "../../icons";
import Cookies from "js-cookie";
import "./../../styles/common/breadcrumbs.scss";

const Breadcrumbs = ({
  links,
  showActions = false,
  actionContext,
  courseKey,
  subjectKey,
  topicKey,
  subTopicKey,
  onCreateSuccess,
  showGoUpButton = false,
  goUpPath = null,
  breadcrumbData = null,
  showDropdowns = true,
}) => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [topics, setTopics] = useState([]);
  const [subTopics, setSubTopics] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const role = Cookies.get("role");

  const basePath = useMemo(() => {
    if (links?.[0]?.path?.startsWith("/institution")) {
      return "/institution";
    }
    if (links?.[0]?.path?.startsWith("/admin")) {
      if (links?.[0]?.path?.startsWith("/admin/materials")) {
        return "/admin/materials";
      } else if (links?.[0]?.path?.startsWith("/admin/question-bank")) {
        return "/admin/question-bank";
      } else {
        return "/admin";
      }
    }
    return role === "institution" ? "/institution" : "/admin";
  }, [links, role]);

  const courseSegment = useMemo(() => {
    const isEval = (links || []).some(
      (l) => typeof l?.path === "string" && l.path.includes("/eval/"),
    );
    if (isEval) return "eval";
    return "course";
  }, [links]);

  const handleNavigate = (level, item) => {
    let targetPath = null;

    switch (level) {
      case 1:
        targetPath = `${basePath}/${courseSegment}/${item.key}`;
        break;
      case 2:
        if (!courseKey) return;
        targetPath = `${basePath}/${courseSegment}/${courseKey}/subject/${item.key}`;
        break;
      case 3:
        if (!courseKey || !subjectKey) return;
        targetPath = `${basePath}/${courseSegment}/${courseKey}/subject/${subjectKey}/topic/${item.key}`;
        break;
      case 4:
        if (!courseKey || !subjectKey || !topicKey) return;
        targetPath = `${basePath}/${courseSegment}/${courseKey}/subject/${subjectKey}/topic/${topicKey}/subtopic/${item.key}`;
        break;
      default:
        break;
    }

    if (targetPath) {
      navigate(targetPath);
      setExpanded(null);
    }
  };

  const getOptionsForLevel = (level) => {
    switch (level) {
      case 1:
        return courses;
      case 2:
        return subjects;
      case 3:
        return topics;
      case 4:
        return subTopics;
      default:
        return [];
    }
  };

  const isActiveOption = (level, keyValue) => {
    switch (level) {
      case 1:
        return courseKey && keyValue === courseKey;
      case 2:
        return subjectKey && keyValue === subjectKey;
      case 3:
        return topicKey && keyValue === topicKey;
      case 4:
        return subTopicKey && keyValue === subTopicKey;
      default:
        return false;
    }
  };

  const handleAccordionChange = (panel) => (_, isExpanded) => {
    setExpanded(isExpanded ? panel : null);
  };

  const handleGoUp = () => {
    if (goUpPath) {
      navigate(goUpPath);
    } else {
      navigate(-1);
    }
  };

  useEffect(() => {
    if (breadcrumbData) {
      if (breadcrumbData.subject && Array.isArray(breadcrumbData.subject)) {
        const transformedSubjects = breadcrumbData.subject.flatMap(
          (subjectObj) => {
            return Object.entries(subjectObj)
              .filter(([key]) => key !== "next_count" && key !== "page_count")
              .map(([key, name]) => ({
                key: key,
                name: name,
              }));
          },
        );
        setSubjects(transformedSubjects);
      }

      if (
        breadcrumbData.topic &&
        Array.isArray(breadcrumbData.topic) &&
        subjectKey
      ) {
        const topicsForSubject = [];
        breadcrumbData.topic.forEach((topicObj) => {
          if (topicObj[subjectKey] && Array.isArray(topicObj[subjectKey])) {
            topicObj[subjectKey].forEach((topicItem) => {
              Object.entries(topicItem).forEach(
                ([topicKeyInData, topicName]) => {
                  if (
                    topicKeyInData !== "next_count" &&
                    topicKeyInData !== "page_count"
                  ) {
                    topicsForSubject.push({
                      key: topicKeyInData,
                      name: topicName,
                    });
                  }
                },
              );
            });
          }
        });
        setTopics(topicsForSubject);
      }

      if (
        breadcrumbData.subtopic &&
        Array.isArray(breadcrumbData.subtopic) &&
        topicKey
      ) {
        const subtopicsForTopic = [];
        breadcrumbData.subtopic.forEach((subtopicObj) => {
          if (subtopicObj[topicKey] && Array.isArray(subtopicObj[topicKey])) {
            subtopicObj[topicKey].forEach((subtopicItem) => {
              Object.entries(subtopicItem).forEach(
                ([subtopicKeyInData, subtopicName]) => {
                  if (subtopicKeyInData !== "page_count") {
                    subtopicsForTopic.push({
                      key: subtopicKeyInData,
                      name: subtopicName,
                    });
                  }
                },
              );
            });
          }
        });
        setSubTopics(subtopicsForTopic);
      }
    } else {
      setSubjects([]);
      setTopics([]);
      setSubTopics([]);
    }
  }, [breadcrumbData, subjectKey, topicKey]);

  useEffect(() => {
    setCourses([]);
  }, []);

  return (
    <div className="breadcrumbs-container">
      <div className="breadcrumbs">
        {links.map((link, index) => {
          if (link.label === undefined && link.icon === undefined) {
            return null;
          }

          const isHome = index === 0;
          const level = index;
          const options = getOptionsForLevel(level);

          const shouldShowDropdowns = !!breadcrumbData && !!showDropdowns;

          const hasOptions =
            options && options.length > 0 && !isHome && shouldShowDropdowns;

          return (
            <Fragment key={index}>
              {index > 0 && (
                <img
                  src={rightArrow}
                  alt="Expand"
                  className="breadcrumb-separator-icon"
                  style={{ marginLeft: index === 1 ? 0 : 8 }}
                />
              )}

              {isHome ? (
                <div
                  onClick={() => link.path && navigate(link.path)}
                  className={`breadcrumb-item 
                    ${link.path ? "breadcrumb-item--link" : ""} 
                    ${link.active ? "breadcrumb-item--active" : ""}`}
                >
                  {link.icon ? (
                    link.icon
                  ) : (
                    <span className="breadcrumb-text">{link.label}</span>
                  )}
                </div>
              ) : (
                <div className="breadcrumb-accordion">
                  <Accordion
                    disableGutters
                    elevation={0}
                    expanded={expanded === level}
                    onChange={handleAccordionChange(level)}
                  >
                    <AccordionSummary
                      expandIcon={
                        hasOptions ? (
                          <img
                            src={downarrow}
                            alt="Expand"
                            className="breadcrumb-expand-icon"
                          />
                        ) : null
                      }
                      aria-controls={`breadcrumb-panel-${level}-content`}
                      id={`breadcrumb-panel-${level}-header`}
                      onClick={(event) => {
                        if (!hasOptions && link.path) {
                          event.stopPropagation();
                          navigate(link.path);
                        }
                      }}
                    >
                      <div
                        className={`breadcrumb-item 
                          ${link.path ? "breadcrumb-item--link" : ""} 
                          ${link.active ? "breadcrumb-item--active" : ""}`}
                      >
                        {link.icon ? (
                          link.icon
                        ) : (
                          <span className="breadcrumb-text">{link.label}</span>
                        )}
                      </div>
                    </AccordionSummary>

                    {hasOptions && (
                      <AccordionDetails>
                        <div className="breadcrumb-options">
                          {options.map((item) => {
                            const keyValue = item.key || item.id;
                            return (
                              <div
                                key={keyValue}
                                className={`breadcrumb-option ${
                                  isActiveOption(level, keyValue)
                                    ? "breadcrumb-option--active"
                                    : ""
                                }`}
                                onClick={() =>
                                  keyValue ? handleNavigate(level, item) : null
                                }
                              >
                                {item.name || item.label}
                              </div>
                            );
                          })}
                        </div>
                      </AccordionDetails>
                    )}
                  </Accordion>
                </div>
              )}
            </Fragment>
          );
        })}
      </div>
      <div className="breadcrumbs-actions">
        {/* {showGoUpButton && (
          <button className="go-up-button" onClick={handleGoUp}>
            <img src={ArrowUpwardIcon} alt="Go Up" className="go-up-icon" />
            <span className="go-up-text">Go up 1 level</span>
          </button>
        )} */}

        {showActions && (
          <ActionButtons
            context={actionContext}
            courseKey={courseKey}
            subjectKey={subjectKey}
            topicKey={topicKey}
            subTopicKey={subTopicKey}
            onCreateSuccess={onCreateSuccess}
          />
        )}
      </div>
    </div>
  );
};

export default Breadcrumbs;
